import type React from 'react';
import {Box, Typography} from '@mui/material';
import {ClickToEdit} from '~/components/ClickToEdit/ClickToEdit';
import {parseMoney} from '../../../domain/planText';
import type {PlanContent} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {useOpenProfileTab} from '../../../hooks/useOpenProfileTab';
import {HelpTitle} from '../SectionLinks';

const META_HELP = 'Drafted from the category: the name and scope from the taxonomy, coverage from the three countries '
    + 'with most spend, influenceable spend from the spend period in the settings, the manager from the team (else the '
    + 'owner) and the sponsor from the owner. The horizon follows the fiscal-year setting. Click any value to change it '
    + 'here; the team and owner themselves are kept on the Overview tab.';

type TextField = Exclude<keyof PlanContent['meta'], 'influenceableSpend'>;

const FIELDS: [TextField | 'influenceableSpend', string][] = [
    ['name', 'Category name'], ['coverage', 'Geographical coverage'], ['scope', 'Category scope'],
    ['influenceableSpend', 'Influenceable spend'], ['manager', 'Category manager'], ['sponsor', 'Category sponsor'],
    ['horizon', 'Planning horizon'], ['reviewCadence', 'Review cadence'],
];

/** Category plan metadata (spec 4.5). Every value is inline-editable. */
export const PlanMetaSection: React.FC<PlanProps> = ({plan, actions, money}) => {
    const meta = plan.content.meta;
    const openTab = useOpenProfileTab();
    const save = (k: keyof PlanContent['meta'], v: string | number) =>
        void actions.save('meta', {...meta, [k]: v}, 'Plan details updated.');

    return (
        <PlanSection title={<HelpTitle title="Category plan" help={META_HELP}
                                       links={[{label: 'the category team on Overview', onClick: () => openTab('overview')}]}/>}
                     edited={plan.edited.includes('meta')}>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)'}, gap: 2}}>
                {FIELDS.map(([k, label]) => (
                    <Box key={k} sx={{minWidth: 0}}>
                        <Typography variant="overline" color="text.secondary" sx={{display: 'block'}}>{label}</Typography>
                        {k === 'influenceableSpend' ? (
                            <ClickToEdit text={money(meta.influenceableSpend)} readOnly={!plan.canEdit}
                                         typographyProps={{variant: 'body2'}}
                                         onSave={v => {
                                             const n = parseMoney(v);
                                             if (n !== null && n !== meta.influenceableSpend) save(k, n);
                                         }}/>
                        ) : (
                            <ClickToEdit text={meta[k]} readOnly={!plan.canEdit} placeholder="Not set"
                                         typographyProps={{variant: 'body2'}}
                                         onSave={v => v.trim() !== meta[k] && save(k, v.trim())}/>
                        )}
                    </Box>
                ))}
            </Box>
        </PlanSection>
    );
};
